import type { Event } from '../types/event';

/**
 * Catálogo de eventos da Teia de Eventos.
 * Fonte de verdade usada em todas as páginas.
 * Basta adicionar novos objetos aqui para que apareçam automaticamente
 * na listagem, nos cards da Home e no roteamento dinâmico.
 */
export const events: Event[] = [
  {
    slug: 'fepro',
    name: 'FEPRO',
    tagline: 'Promoção e comodidade',
    shortDescription:
      'A maior feira de promoção da Região Serrana do Rio de Janeiro. Onde empresas se encontram, parcerias nascem e a economia local pulsa.',
    fullDescription: [
      'A FEPRO é um evento único e consolidado, realizado há 26 anos em Nova Friburgo. Bate recorde de público a cada edição e se tornou referência no calendário de feiras do interior do Rio de Janeiro — um encontro entre consumidores, lojistas e indústrias que movimenta a economia da Região Serrana como nenhum outro evento.',
      'O objetivo é simples e poderoso: proporcionar ao consumidor acesso a preços abaixo do mercado tradicional, com descontos que chegam a 70%. Em um único espaço, o visitante encontra desde moda e calçados até eletrodomésticos, móveis, alimentos e muito mais — tudo com condições especiais disponíveis apenas durante os dias da feira.',
      'Ao longo de sua trajetória, a FEPRO reuniu mais de 1.000.000 de visitantes, envolveu mais de 4.000 empresas e gerou mais de 50 mil empregos diretos e indiretos. Números que traduzem o impacto real de um evento que já faz parte da história e da identidade de Nova Friburgo.',
    ],
    category: 'varejo',
    status: 'past',
    dateLabel: 'Abril',
    startDate: '2026-04-08',
    endDate: '2026-04-12',
    location: { name: 'Nova Friburgo Country Clube', city: 'Nova Friburgo', state: 'RJ' },
    featured: true,
    accentColor: 'navy',
    tags: ['promoções', 'varejo', 'networking', 'feira'],
    expectedAudience: '40.000+ visitantes',
    cover: '/images/FeproCover.jpg',
  },
  {
    slug: 'salao-noivas-festas',
    name: 'Salão Noivas & Festas',
    tagline: 'Tudo para o seu grande momento',
    shortDescription:
      'O evento de referência para noivos, famílias e profissionais do mercado de casamentos e celebrações na Região Serrana.',
    fullDescription: [
      'Realizado em conjunto com a FEPRO, o Salão de Noivas e Festas é voltado ao público que busca serviços e produtos para casamentos e celebrações. Reúne fornecedores de vestidos, decoração, buffet, fotografia, música e diversos outros segmentos — tudo para que o visitante encontre em um único local soluções completas para organizar o grande dia.',
      'O Salão de Noivas e Festas tem como local o Nova Friburgo Country Clube — o jardim histórico mais bem preservado do país. Um cenário à altura de um evento que faz parte do calendário anual da cidade e que, a cada edição, reafirma seu lugar como referência no mercado de casamentos e celebrações da Região Serrana.',
      'O sucesso do Salão se deve à atenção aos detalhes e à expertise dos expositores que, ano após ano, apresentam as tendências do mercado da forma mais profissional e encantadora possível. Para quem está planejando o grande dia, é uma visita obrigatória.',
    ],
    category: 'exposicao',
    status: 'past',
    dateLabel: 'Abril',
    startDate: '2026-04-08',
    endDate: '2026-04-12',
    location: { name: 'Nova Friburgo Country Clube', city: 'Nova Friburgo', state: 'RJ' },
    featured: false,
    accentColor: 'cyan',
    tags: ['casamento', 'festa', 'noivas', 'celebração'],
    expectedAudience: '40.000+ visitantes',
    cover: '/images/SalaoNoivas&FestasCover.jpg',
  },
  {
    slug: 'nossa-feira',
    name: 'Nossa Feira',
    tagline: 'Comércio local, ambiente aconchegante',
    shortDescription:
      'Mercado de produtores que celebra o que há de mais autêntico na agricultura, artesanato e gastronomia da Região Serrana.',
    fullDescription: [
      'A Nossa Feira foi idealizada a partir de um desejo genuíno: criar um evento acessível, autêntico e com impacto real na economia local. Em sua primeira edição em 2024, surpreendeu ao movimentar milhares de pessoas, atrair famílias inteiras e conectar negócios de diferentes setores da Região Serrana.',
      'O nome NOSSA não é por acaso. Ele representa pertencimento, colaboração e comunidade — a ideia de que este evento é de todos, feito para todos e construído junto com quem faz Nova Friburgo pulsar todos os dias.',
      'Em 2025, a Nossa Feira se consolidou como uma das maiores vitrines de negócios e experiências da Região Serrana, reunindo expositores, produtores locais, empreendedores e o público em torno de algo que vai além de uma feira — uma celebração do que é nosso.',
    ],
    category: 'varejo',
    status: 'upcoming',
    dateLabel: 'Julho/Agosto',
    startDate: '2026-07-29',
    endDate: '2026-08-02',
    location: { name: 'Nova Friburgo Country Clube', city: 'Nova Friburgo', state: 'RJ' },
    featured: true,
    accentColor: 'steel',
    tags: ['comércio local', 'orgânicos', 'artesanato', 'gastronomia'],
    expectedAudience: '40.000+ visitantes por edição',
    cover: '/images/NossaFeiraCover.jpg',
  },
  {
    slug: 'festa-do-morango',
    name: 'Festa do Morango com Chocolate',
    tagline: 'Uma combinação que vicia',
    shortDescription:
      'Celebração do maior produto agrícola da Região Serrana — o morango — em um festival gastronômico que atrai visitantes de todo o estado.',
    fullDescription: [
      'A Festa do Morango com Chocolate acontece há 11 anos em Nova Friburgo, com sucesso de público e críticas em todas as edições — consolidando-se como um evento tradicional no calendário do município no mês de outubro e um dos mais aguardados da Região Serrana.',
      'Nova Friburgo é a maior produtora de morangos do estado do Rio de Janeiro. Unindo esse fato à herança suíça do consumo do chocolate, surgiu este saboroso evento que recebe visitantes de todo o Brasil em busca de uma das combinações mais amadas da gastronomia nacional.',
      'Além de desfrutar dessa combinação perfeita de sabores, o visitante pode participar de oficinas e workshops, e assistir a palestras e shows que fazem da Festa do Morango com Chocolate um evento diversificado, familiar e absolutamente imperdível.',
    ],
    category: 'gastronomico',
    status: 'upcoming',
    dateLabel: 'Outubro',
    startDate: '2026-10-08',
    endDate: '2026-10-12',
    location: { name: 'Nova Friburgo Country Clube', city: 'Nova Friburgo', state: 'RJ' },
    featured: true,
    accentColor: 'cyan',
    tags: ['morango', 'chocolate', 'gastronomia', 'festa'],
    expectedAudience: '40.000+ visitantes',
    cover: '/images/FestaDoMorangoComChocolateCover.jpg',
  },
  {
    slug: 'festa-da-flor',
    name: 'Festa da Flor',
    tagline: 'A beleza que nasce nas montanhas',
    shortDescription:
      'Homenagem à tradição floricultura de Nova Friburgo — a capital da flor do Brasil — em um festival que transforma a cidade num jardim a céu aberto.',
    fullDescription: [
      'Junto à Festa do Morango com Chocolate, acontece também a tradicional Festa da Flor, no Salão Social do clube. O evento reúne produtores de diferentes pontos do município com o objetivo de proporcionar ao visitante uma experiência única de contemplação e compra de diferentes espécies — um verdadeiro jardim a céu aberto no coração de Nova Friburgo.',
      'Além de valorizar a produção de flores no município, que se consolida como o maior produtor de flores de corte do estado do Rio de Janeiro, o evento movimenta a economia da região e conta com comércio de flores e plantas, oficinas de artes florais, espaços instagramáveis e muito mais.',
      'Qualquer amante de floricultura, jardinagem e paisagismo fica encantado com as variedades oferecidas — incluindo espécimes exclusivos da Mata Atlântica que não se encontram em qualquer lugar. Uma experiência sensorial que une beleza, cultura e o melhor que a natureza da Serra Fluminense tem a oferecer.',
    ],
    category: 'exposicao',
    status: 'upcoming',
    dateLabel: 'Outubro',
    startDate: '2026-10-08',
    endDate: '2026-10-12',
    location: { name: 'Nova Friburgo Country Clube', city: 'Nova Friburgo', state: 'RJ' },
    featured: true,
    accentColor: 'cyan',
    tags: ['flores', 'floricultura', 'natureza', 'cultura'],
    expectedAudience: '40.000+ visitantes',
    cover: '/images/FestaDaFlorCover.jpg',
  },
  {
    slug: 'festival-comida-de-boteco',
    name: 'Festival de Comida de Boteco',
    tagline: 'Petisco bom e cerveja gelada',
    shortDescription:
      'Disputa gastronômica entre os melhores bares e restaurantes da cidade, com os petiscos mais criativos e gostosos da Região Serrana.',
    fullDescription: [
      'O Festival de Comida de Boteco é um evento que celebra a cultura do boteco nacional, reunindo diversos estabelecimentos em um único lugar. Uma festa regada a muita comida boa, cerveja gelada e samba — do jeito que o brasileiro gosta.',
      'Cada estabelecimento participante traz o que tem de melhor: petiscos autorais, receitas de família e criações exclusivas desenvolvidas especialmente para o festival. O público prova, avalia e elege seus favoritos, tornando cada edição uma disputa acirrada e muito saborosa.',
      'Mais do que uma competição gastronômica, o Festival de Comida de Boteco é um encontro de pessoas, sabores e histórias — uma celebração da simplicidade e da alegria que só um bom boteco sabe proporcionar.',
    ],
    category: 'festival',
    featured: false,
    status: 'past',
    dateLabel: 'Abril/Maio',
    startDate: '2024-04-20',
    endDate: '2024-05-31',
    location: { name: 'Bares participantes', city: 'Nova Friburgo', state: 'RJ' },
    accentColor: 'navy',
    tags: ['gastronomia', 'bares', 'petisco', 'festival'],
    expectedAudience: '20.000+ visitantes',
    cover: '/images/FestivalDeComidaDeBotecoCover.jpg',
  },
  {
    slug: 'festival-de-truta',
    name: 'Festival de Truta',
    tagline: 'O sabor da serra',
    shortDescription:
      'Celebração da truticultara de Nova Friburgo, reunindo os melhores pratos e produtores em um festival gastronômico único na Região Serrana.',
    fullDescription: [
      'Prepare-se para uma jornada gastronômica única, com pratos incríveis desenvolvidos exclusivamente para o Festival da Truta. Chefs talentosos da região criaram receitas irresistíveis que destacam a autenticidade e o frescor das trutas locais — um cardápio especial que só existe durante os dias do evento.',
      'Consulte a lista de restaurantes participantes e mergulhe em uma experiência culinária que desperta todos os sentidos. Deixe-se levar pela combinação perfeita de sabores e aromas, enquanto desfruta da atmosfera encantadora da Serra de Nova Friburgo.',
      'Marque seus amigos que adoram boa comida e natureza — não perca a chance de fazer parte deste evento gastronômico memorável. Venha se deliciar conosco!',
    ],
    category: 'festival',
    status: 'past',
    dateLabel: 'Novembro/Dezembro',
    startDate: '2023-11-27',
    endDate: '2023-12-10',
    location: { name: 'Restaurantes participantes', city: 'Nova Friburgo', state: 'RJ' },
    featured: false,
    accentColor: 'steel',
    tags: ['truta', 'gastronomia', 'serra', 'festival'],
    expectedAudience: '15.000+ visitantes',
    cover: '/images/FestivalDeTrutaCover.jpg',
  },
];

/**
 * Encontra um evento pelo slug.
 */
export function getEventBySlug(slug: string): Event | undefined {
  return events.find((e) => e.slug === slug);
}

/**
 * Retorna eventos ordenados por data de início (mais recentes primeiro).
 */
export function getEventsSorted(count = 4): Event[] {
  return [...events]
    .filter((e) => e.featured)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    .slice(0, count);
}

/**
 * Retorna os N eventos em destaque para a Home.
 */
export function getFeaturedEvents(count = 4): Event[] {
  return getEventsSorted().slice(0, count);
}
